import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Chip,
  Stack,
  Alert,
  InputAdornment,
  Switch,
  FormControlLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import ListIcon from '@mui/icons-material/List';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate, useLocation } from 'react-router-dom';
import Api from '../../Services/Api';
import PageHeader from './PageHeader';
import RsocNavHeader from './RsocNavHeader';

// Api is already an instance, no need to instantiate
// const api = new Api();

const RsocGenerator = () => {
  // React Router navigation
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for form fields
  const [domainId, setDomainId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [title, setTitle] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [currentKeyword, setCurrentKeyword] = useState('');
  const [layoutConfigId, setLayoutConfigId] = useState(4); // Default layout config ID - always use 4
  const [numSections] = useState(10); // Always use 10 sections
  
  // Generated content state
  const [generatedContent, setGeneratedContent] = useState(null);
  const [intro, setIntro] = useState('');
  const [contentBlocks, setContentBlocks] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Options for dropdowns
  const [domains, setDomains] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [filteredCategoryOptions, setFilteredCategoryOptions] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [loadingCategoryOptions, setLoadingCategoryOptions] = useState(false);
  const [domainsError, setDomainsError] = useState(null);
  const [categoriesError, setCategoriesError] = useState(null);
  
  // Additional state for category search
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  
  // Always use OpenAI (removed toggle)
  const useOpenAI = true;
  
  // Fetch domains from ads.com API
  const fetchDomains = async () => {
    setLoadingDomains(true);
    setDomainsError(null);
    
    try {
      const response = await Api.searchDomains();
      
      if (response && response.data) {
        // Map the domains to the expected format
        const mappedDomains = response.data.map(domain => ({
          id: domain.domain_name_id,
          name: domain.domain_name
        }));
        
        setDomains(mappedDomains);
      } else {
        setDomainsError('Failed to fetch domains');
      }
    } catch (err) {
      console.error('Error fetching domains:', err);
      setDomainsError('Error fetching domains: ' + (err.message || 'Unknown error'));
    } finally {
      setLoadingDomains(false);
    }
  };
  
  // Fetch data on component mount
  useEffect(() => {
    fetchDomains();
    searchCategories();
  }, []);
  
  // Handle pre-filled data from navigation state
  useEffect(() => {
    if (location.state) {
      // Handle legacy prefilled keywords (limit to 5 for API compliance)
      if (location.state.prefilledKeywords && Array.isArray(location.state.prefilledKeywords)) {
        setKeywords(location.state.prefilledKeywords.slice(0, 5));
      }
      
      // Handle article prefill data from recommended articles
      if (location.state.prefillData) {
        const data = location.state.prefillData;
        
        // Pre-fill form fields
        if (data.domainId) setDomainId(data.domainId);
        if (data.title) setTitle(data.title);
        if (data.keywords && Array.isArray(data.keywords)) setKeywords(data.keywords.slice(0, 5)); // Limit to 5 for API compliance
        
        // Pre-fill generated content
        if (data.generatedContent) {
          setGeneratedContent(data.generatedContent);
          if (data.generatedContent.intro) setIntro(data.generatedContent.intro);
          if (data.generatedContent.contentBlocks) setContentBlocks(data.generatedContent.contentBlocks);
        }
      }
      if (location.state.prefilledTitle) {
        setTitle(location.state.prefilledTitle);
      }
    }
  }, [location.state]);

  // Separate effect to handle category pre-filling by searching via API
  // Wait for the main categories to load first, then do the prefill
  useEffect(() => {
    const searchAndSetCategory = async () => {
      if (location.state?.prefillData?.categoryName && categoryOptions.length > 0) {
        const data = location.state.prefillData;
        console.log('Searching for category via API (after main load):', data.categoryName);
        
        try {
          // Use the verticals/search endpoint to find the category
          const response = await Api.searchVerticals({
            filter_by: [
              {
                column: 'name',
                operator: '=',
                value: data.categoryName
              }
            ]
          });
          
          if (response && response.data && response.data.length > 0) {
            const matchingCategory = response.data[0];
            console.log('Found exact match via API search:', matchingCategory);
            
            // Create the category option in the correct format
            const newOption = {
              id: matchingCategory.id,
              name: matchingCategory.name,
              parent_id: matchingCategory.parent_id || null
            };
            
            // Check if category already exists in the loaded options
            const existingCategory = categoryOptions.find(cat => cat.id === matchingCategory.id);
            if (existingCategory) {
              console.log('Category already exists in loaded options:', existingCategory);
              // Just set the ID since it's already in the options
              setCategoryId(String(matchingCategory.id));
              // Also update the categories array for the save function
              setCategories([matchingCategory.name]);
            } else {
              console.log('Adding new category to options:', newOption);
              // Add to both categoryOptions and filteredCategoryOptions
              setCategoryOptions(prev => {
                const updated = [...prev, newOption];
                console.log('Updated categoryOptions count:', updated.length);
                return updated;
              });
              setFilteredCategoryOptions(prev => {
                const updated = [...prev, newOption];
                console.log('Updated filteredCategoryOptions count:', updated.length);
                console.log('New category in filtered options?', updated.some(cat => String(cat.id) === String(matchingCategory.id)));
                return updated;
              });
              setCategoryId(String(matchingCategory.id));
              // Also update the categories array for the save function
              setCategories([matchingCategory.name]);
              console.log('Category should now be selected with ID:', String(matchingCategory.id));
            }
          } else {
            console.log('No exact match found, trying partial search...');
            
            // Try partial search as fallback
            const partialResponse = await Api.searchVerticals({
              filter_by: [
                {
                  column: 'name',
                  operator: 'LIKE',
                  value: `%${data.categoryName}%`
                }
              ]
            });
            
            if (partialResponse && partialResponse.data && partialResponse.data.length > 0) {
              const partialMatch = partialResponse.data[0];
              console.log('Found partial match via API search:', partialMatch);
              
              // Check if category already exists in the loaded options
              const existingCategory = categoryOptions.find(cat => cat.id === partialMatch.id);
              if (existingCategory) {
                console.log('Partial match already exists in loaded options:', existingCategory);
                setCategoryId(String(partialMatch.id));
                // Also update the categories array for the save function
                setCategories([partialMatch.name]);
              } else {
                const newOption = {
                  id: partialMatch.id,
                  name: partialMatch.name,
                  parent_id: partialMatch.parent_id || null
                };
                console.log('Adding partial match category to options:', newOption);
                setCategoryOptions(prev => [...prev, newOption]);
                setFilteredCategoryOptions(prev => [...prev, newOption]);
                setCategoryId(String(partialMatch.id));
                // Also update the categories array for the save function
                setCategories([partialMatch.name]);
              }
            } else {
              console.log('No category found via API search for:', data.categoryName);
            }
          }
        } catch (err) {
          console.error('Error searching for category:', err);
        }
      }
    };
    
    // Only run if we have prefill data, haven't set a category, AND main categories are loaded
    if (location.state?.prefillData?.categoryName && !categoryId && categoryOptions.length > 0) {
      searchAndSetCategory();
    }
  }, [location.state, categoryId, categoryOptions, filteredCategoryOptions]);

  // Debug effect to monitor category selection state
  useEffect(() => {
    if (categoryId && location.state?.prefillData?.categoryName) {
      console.log('Category selection state check:');
      console.log('- categoryId:', categoryId);
      console.log('- filteredCategoryOptions count:', filteredCategoryOptions.length);
      console.log('- Category exists in filtered options?', 
        filteredCategoryOptions.some(cat => String(cat.id) === String(categoryId)));
      
      const matchingOption = filteredCategoryOptions.find(cat => String(cat.id) === String(categoryId));
      if (matchingOption) {
        console.log('- Matching option found:', matchingOption);
      } else {
        console.log('- No matching option found for categoryId:', categoryId);
        console.log('- Available IDs:', filteredCategoryOptions.map(cat => String(cat.id)).slice(0, 10));
      }
    }
  }, [categoryId, filteredCategoryOptions, location.state]);
  
  // Handle adding a keyword
  const handleAddKeyword = () => {
    if (currentKeyword && !keywords.includes(currentKeyword) && !currentKeyword.includes(',')) {
      setKeywords([...keywords, currentKeyword]);
      setCurrentKeyword('');
    } else if (currentKeyword.includes(',')) {
      setError('Keywords cannot contain commas');
    }
  };
  
  // Handle removing a keyword
  const handleRemoveKeyword = (keywordToRemove) => {
    setKeywords(keywords.filter(keyword => keyword !== keywordToRemove));
  };
  
  // Handle title edit
  const handleTitleChange = (e) => {
    setTitle(e.target.value);
  };
  
  // Handle intro edit
  const handleIntroChange = (e) => {
    setIntro(e.target.value);
  };
  
  // Handle content block edit
  const handleContentBlockChange = (index, field, value) => {
    const updatedBlocks = [...contentBlocks];
    updatedBlocks[index] = {
      ...updatedBlocks[index],
      [field]: value
    };
    setContentBlocks(updatedBlocks);
  };
  
  // Format content for display
  const formatContentForDisplay = (responseData) => {
    if (!responseData) return null;
    
    // Function to remove surrounding quotes from a string
    const removeQuotes = (str) => {
      if (typeof str === 'string') {
        // Remove surrounding quotes if they exist
        return str.replace(/^["'](.*)["']$/, '$1');
      }
      return str;
    };
    
    setIntro(removeQuotes(responseData.intro) || '');
    
    // Set title if available
    if (responseData.title) {
      // Limit title to 100 characters to comply with API constraints
      setTitle(removeQuotes(responseData.title).slice(0, 100));
    }
    
    // Set keywords if available (limit to 5 for API compliance)
    if (responseData.keywords && Array.isArray(responseData.keywords)) {
      setKeywords(responseData.keywords.slice(0, 5));
    }
    
    // Get selected category name for proper category handling
    const selectedCategory = filteredCategoryOptions.find(cat => cat.id === parseInt(categoryId));
    const selectedCategoryName = selectedCategory ? selectedCategory.name : null;
    
    // Set categories with priority order:
    // 1. Array of categories from response
    // 2. Single category property from response
    // 3. Selected category from the dropdown
    // 4. Default from keywords
    if (responseData.categories && Array.isArray(responseData.categories) && responseData.categories.length > 0) {
      console.log('Using categories array from response:', responseData.categories);
      setCategories(responseData.categories);
    } else if (responseData.category) {
      console.log('Using category string from response:', responseData.category);
      setCategories([responseData.category]);
    } else if (selectedCategoryName) {
      console.log('Using selected category name:', selectedCategoryName);
      setCategories([selectedCategoryName]);
    } else {
      // Last resort - set a default category based on keywords
      const defaultCategory = keywords.length > 0 ? 
        keywords[0].charAt(0).toUpperCase() + keywords[0].slice(1) : 'General';
      console.log('Using default category from keywords:', defaultCategory);
      setCategories([defaultCategory]);
    }
    
    // Process content blocks
    if (responseData.content) {
      // Function to remove surrounding quotes from a string
      const removeQuotes = (str) => {
        if (typeof str === 'string') {
          // Remove surrounding quotes if they exist
          return str.replace(/^["'](.*)["']$/, '$1');
        }
        return str;
      };
      
      // Check if content is an array (new format)
      if (Array.isArray(responseData.content)) {
        // Clean up quotes from headers and paragraphs
        const cleanedContent = responseData.content.map(block => ({
          header: removeQuotes(block.header),
          paragraph: removeQuotes(block.paragraph)
        }));
        setContentBlocks(cleanedContent);
      } else if (typeof responseData.content === 'object') {
        const blocks = [];
        
        if (responseData.content.header && responseData.content.intro) {
          // API returned single content block format
          blocks.push({
            header: removeQuotes(responseData.content.header),
            paragraph: removeQuotes(responseData.content.intro)
          });
        } else {
          // Parse content keys (probably multiple sections)
          Object.keys(responseData.content).forEach(key => {
            if (key !== 'header' && key !== 'intro' && typeof responseData.content[key] === 'string') {
              blocks.push({
                header: removeQuotes(key),
                paragraph: removeQuotes(responseData.content[key])
              });
            }
          });
        }
        
        setContentBlocks(blocks);
      }
    }
    
    return responseData;
  };
  
  // Handle form submission to generate content
  const handleGenerateContent = async () => {
    if (!domainId || !categoryId) {
      setError('Domain and Category are required');
      return;
    }
    
    if (keywords.length < 3) {
      setError('At least 3 keywords are required');
      return;
    }
    
    if (!title || title.trim() === '') {
      setError('Title is required');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    // Reset content state
    setGeneratedContent(null);
    setIntro('');
    setContentBlocks([]);
    
    try {
      // Include category_id in the request to ensure it's available for saving later
      const requestData = {
        type: "page", // Required field for the API
        params: {
          title: title,
          keywords: keywords.slice(0, 5), // Limit keywords to 5 items to comply with ads.com API validation
          category_id: parseInt(categoryId), // Include category ID for later use
          domain_name_id: parseInt(domainId), // Include domain ID for later use
          sections: numSections // Include number of sections selected by user
        }
      };
      
      // Always use OpenAI
      const response = await Api.generateRsocPageWithOpenAI(requestData);
      
      if (response && response.success && response.data) {
        const formattedContent = formatContentForDisplay(response.data);
        setGeneratedContent(formattedContent);
        setSuccess('Content generated successfully!');
      } else {
        // Improved error handling for validation errors
        if (response && response.errors) {
          // Format validation errors
          const errorMessages = [];
          for (const field in response.errors) {
            if (response.errors[field] && Array.isArray(response.errors[field])) {
              errorMessages.push(...response.errors[field]);
            }
          }
          setError(errorMessages.join('\n') || 'Validation failed. Please check your inputs.');
        } else {
          setError(response?.message || 'Failed to generate content. Please try again.');
        }
      }
    } catch (err) {
      // Check if response contains validation errors
      if (err.response && err.response.data && err.response.data.errors) {
        const errorMessages = [];
        for (const field in err.response.data.errors) {
          if (err.response.data.errors[field] && Array.isArray(err.response.data.errors[field])) {
            errorMessages.push(...err.response.data.errors[field]);
          }
        }
        setError(errorMessages.join('\n') || 'Validation failed. Please check your inputs.');
      } else {
        setError(err.message || 'An error occurred while generating content');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Handle saving the generated content
  const handleSaveContent = async () => {
    if (!generatedContent) {
      setError('No content to save. Please generate content first.');
      return;
    }
    
    // Validate all required fields
    let validationErrors = [];
    
    if (!title || title.trim() === '') {
      validationErrors.push('Title is required');
    }
    
    if (!intro || intro.trim() === '') {
      validationErrors.push('Introduction is required');
    }
    
    if (keywords.length < 3) {
      validationErrors.push('At least 3 keywords are required');
    }
    
    if (contentBlocks.length === 0) {
      validationErrors.push('At least one content section is required');
    } else {
      // Validate each content block
      contentBlocks.forEach((block, index) => {
        if (!block.header || block.header.trim() === '' || block.header.length < 5) {
          validationErrors.push(`Section ${index + 1}: Header must be at least 5 characters`);
        }
        
        if (!block.paragraph || block.paragraph.trim() === '' || block.paragraph.length < 250) {
          validationErrors.push(`Section ${index + 1}: Content must be at least 250 characters`);
        }
      });
    }
    
    if (validationErrors.length > 0) {
      setError(validationErrors.join('\n'));
      return;
    }
    
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Get the selected category text from the categoryId
      console.log('Save: Looking for category with ID:', categoryId, 'type:', typeof categoryId);
      console.log('Save: Available categories:', filteredCategoryOptions.map(cat => ({id: cat.id, name: cat.name, type: typeof cat.id})));
      
      const selectedCategory = filteredCategoryOptions.find(cat => String(cat.id) === String(categoryId));
      const categoryName = selectedCategory ? selectedCategory.name : null;
      
      console.log('Save: Selected category:', selectedCategory);
      console.log('Save: Category name to send:', categoryName);
      
      // Prepare the content blocks in the format expected by the API
      const formattedContent = contentBlocks.map(block => ({
        header: block.header,
        paragraph: block.paragraph
      }));
      
      const requestData = {
        type: "page", // Required field for the API
        params: {
          domain_name_id: parseInt(domainId),
          title: String(title || ''),
          intro: intro,
          content: formattedContent,
          keywords: keywords.slice(0, 5), // Limit keywords to 5 items to comply with ads.com API validation
          categories: categoryName ? [categoryName] : categories, // Use selected category name if available
          rsoc_layout_configuration_id: 4  // Always use layout 4
        }
      };
      
      const response = await Api.storeRsocPage(requestData);
      
      if (response && response.success) {
        setSuccess('Content saved successfully!');
        
        // Wait a brief moment to show the success message before redirecting
        setTimeout(() => {
          navigate('/rsoc/pages');
        }, 1000);
      } else {
        // Improved error handling for validation errors
        if (response && response.errors) {
          // Format validation errors
          const errorMessages = [];
          for (const field in response.errors) {
            if (response.errors[field] && Array.isArray(response.errors[field])) {
              errorMessages.push(...response.errors[field]);
            }
          }
          setError(errorMessages.join('\n') || 'Validation failed. Please check your inputs.');
        } else {
          setError(response?.message || 'Failed to save content. Please try again.');
        }
      }
    } catch (err) {
      // Check if response contains validation errors
      if (err.response && err.response.data && err.response.data.errors) {
        const errorMessages = [];
        for (const field in err.response.data.errors) {
          if (err.response.data.errors[field] && Array.isArray(err.response.data.errors[field])) {
            errorMessages.push(...err.response.data.errors[field]);
          }
        }
        setError(errorMessages.join('\n') || 'Validation failed. Please check your inputs.');
      } else {
        setError(err.message || 'An error occurred while saving content');
      }
    } finally {
      setSaving(false);
    }
  };
  
  // Copy content to clipboard
  const handleCopyContent = () => {
    if (generatedContent) {
      // Create a text representation of the content
      let textContent = `Title: ${title}\n\n`;
      textContent += `Intro: ${intro}\n\n`;
      
      contentBlocks.forEach(block => {
        textContent += `${block.header}\n\n${block.paragraph}\n\n`;
      });
      
      navigator.clipboard.writeText(textContent)
        .then(() => setSuccess('Content copied to clipboard!'))
        .catch(err => setError('Failed to copy content: ' + err.message));
    }
  };
  
  // Add a new empty content block
  const handleAddContentBlock = () => {
    setContentBlocks([
      ...contentBlocks,
      {
        header: 'New Section',
        paragraph: ''
      }
    ]);
  };
  
  // Remove a content block
  const handleRemoveContentBlock = (indexToRemove) => {
    setContentBlocks(contentBlocks.filter((_, index) => index !== indexToRemove));
  };
  
  // Función para buscar categorías
  const searchCategories = async (query = '') => {
    setLoadingCategoryOptions(true);
    
    try {
      // Create parameters for the search
      const params = {
        order_by: [{ column: "name", order: "asc" }],
        page: 1,
        per_page: 100
      };
      
      // If there's a search query, add it to the filter_by parameter
      if (query && query.trim() !== '') {
        params.filter_by = [
          {
            column: "name",
            operator: "like",
            value: `%${query}%`
          }
        ];
      }
      
      // Make the API call with the filter
      const response = await Api.searchVerticals(params);
      
      if (response && response.data) {
        // Map categories to the expected format
        const mappedCategories = response.data.map(category => ({
          id: category.id,
          name: category.name,
          parent_id: category.parent_id || null
        }));
        
        // Store all categories
        if (!query) {
          console.log('Loaded categories:', mappedCategories.length, 'categories');
          setCategoryOptions(prev => {
            // Merge with existing categories, avoiding duplicates
            const existingIds = prev.map(cat => cat.id);
            const newCategories = mappedCategories.filter(cat => !existingIds.includes(cat.id));
            return [...prev, ...newCategories];
          });
          setFilteredCategoryOptions(prev => {
            // Merge with existing filtered categories, avoiding duplicates
            const existingIds = prev.map(cat => cat.id);
            const newCategories = mappedCategories.filter(cat => !existingIds.includes(cat.id));
            return [...prev, ...newCategories];
          });
        } else {
          // Even with API filtering, also filter client-side as a safety measure
          const filtered = mappedCategories.filter(category => 
            category.name.toLowerCase().includes(query.toLowerCase())
          );
          setFilteredCategoryOptions(filtered);
        }
      } else {
        setCategoriesError('No se pudieron cargar las categorías');
        setFilteredCategoryOptions([]);
      }
    } catch (err) {
      console.error('Error buscando categorías:', err);
      setCategoriesError(`Error: ${err.message || 'Error desconocido'}`);
      setFilteredCategoryOptions([]);
    } finally {
      setLoadingCategoryOptions(false);
    }
  };
  
  // Handle category search input change with debounce
  const handleCategorySearchChange = (e) => {
    const query = e.target.value;
    setCategorySearchQuery(query);
    
    // Clear any existing timeout
    if (window.categorySearchTimeout) {
      clearTimeout(window.categorySearchTimeout);
    }
    
    // Set new debounce timeout
    window.categorySearchTimeout = setTimeout(() => {
      searchCategories(query);
    }, 300);
  };
  
  // Clear timeout when component unmounts
  useEffect(() => {
    return () => {
      if (window.categorySearchTimeout) {
        clearTimeout(window.categorySearchTimeout);
      }
    };
  }, []);

  // Component cleanup when unmounting
  useEffect(() => {
    return () => {
      // Clear any pending search timeouts
      if (window.categorySearchTimeout) {
        clearTimeout(window.categorySearchTimeout);
        window.categorySearchTimeout = null;
      }
      
      // Clean up any other global state if needed
      // This prevents memory leaks and state persistence between component mounts
    };
  }, []);
  
  return (
    <Container maxWidth="xl">
      <PageHeader 
        title="RSOC Page Generator"
        subtitle="Create optimized content pages for your domains"
      />
      
      <RsocNavHeader />
      
      <Grid container spacing={2}>
        {/* Form Section */}
        <Grid item xs={12} md={5}>
          <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>
              Page Parameters
            </Typography>
            
            <Box component="form" sx={{ mt: 1 }}>
              {domainsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {domainsError}
                </Alert>
              )}
              
              <FormControl fullWidth margin="normal">
                <InputLabel id="domain-label">Domain</InputLabel>
                <Select
                  labelId="domain-label"
                  value={domainId}
                  label="Domain"
                  onChange={(e) => setDomainId(e.target.value)}
                  required
                  disabled={loadingDomains}
                  startAdornment={loadingDomains ? (
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                  ) : null}
                >
                  {domains.map((domain) => (
                    <MenuItem key={domain.id} value={domain.id}>
                      {domain.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {categoriesError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {categoriesError}
                </Alert>
              )}
              
              <FormControl fullWidth margin="normal">
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  value={categoryId}
                  label="Category"
                  onChange={(e) => setCategoryId(e.target.value)}
                  required
                  disabled={loadingCategoryOptions}
                  MenuProps={{ 
                    PaperProps: { 
                      sx: { maxHeight: 350 } 
                    },
                    // Prevent dropdown from closing when clicking on the search field
                    // or when searching/selecting categories
                    disableRestoreFocus: true,
                    disableAutoFocusItem: true,
                    disablePortal: true,
                    disableScrollLock: false,
                    // This fixes an issue where clicking the search input causes the dropdown to close
                    slotProps: {
                      paper: {
                        onMouseDown: (e) => {
                          // Prevent clicks inside dropdown from closing it
                          e.stopPropagation();
                        }
                      }
                    },
                    // Keep dropdown state when searching
                    onClose: (e) => {
                      // Don't close if the click is inside our search field
                      if (e && e.target && e.target.closest('[role="presentation"]')) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }
                      // We don't reset the search query when closing to maintain the filtered results
                      // This improves UX by keeping the last search when reopening
                    }
                  }}
                  // Display loading indicator in the select
                  renderValue={(selected) => {
                    console.log('renderValue called with:', selected, 'type:', typeof selected);
                    console.log('filteredCategoryOptions for renderValue:', filteredCategoryOptions.length, 'options');
                    console.log('First few options:', filteredCategoryOptions.slice(0, 3).map(cat => ({id: cat.id, name: cat.name, type: typeof cat.id})));
                    
                    const selectedCategory = filteredCategoryOptions.find(cat => String(cat.id) === String(selected));
                    console.log('selectedCategory found:', selectedCategory);
                    
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {loadingCategoryOptions && <CircularProgress size={20} sx={{ mr: 1 }} />}
                        {selectedCategory ? selectedCategory.name : (selected ? `ID: ${selected}` : '')}
                      </Box>
                    );
                  }}
                >
                  {/* Search field with improved event handling to prevent dropdown closing */}
                  <MenuItem 
                    sx={{ 
                      p: 1, 
                      position: 'sticky', 
                      top: 0, 
                      bgcolor: 'background.paper',
                      zIndex: 1,
                      borderBottom: '1px solid #e0e0e0'
                    }}
                    onKeyDown={(e) => {
                      // Prevent any keyboard events from bubbling up to the Select component
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      // Prevent click from bubbling up to the MenuItem and closing the dropdown
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onMouseDown={(e) => {
                      // Critical fix: Prevent mouseDown events from closing the dropdown
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    disableRipple
                    disabled={false}
                    tabIndex={-1}
                    dense
                    divider
                  >
                    <TextField
                      aria-label="Search categories"
                      size="small"
                      placeholder="Search categories..."
                      fullWidth
                      value={categorySearchQuery}
                      // Prevent focus events from closing the dropdown
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      autoFocus
                      onChange={(e) => {
                        // Prevent any potential bubbling
                        e.stopPropagation();
                        handleCategorySearchChange(e);
                      }}
                      onKeyDown={(e) => {
                        // Prevent keyboard events from closing the dropdown
                        e.stopPropagation();
                        // Allow typing to continue
                        if (e.key === 'Escape') {
                          // Let Escape close the dropdown
                          return;
                        }
                        // Prevent Enter from selecting an item
                        if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                          e.preventDefault();
                        }
                      }}
                      onClick={(e) => {
                        // Prevent clicks from closing the dropdown
                        e.stopPropagation();
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: loadingCategoryOptions ? (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ) : null,
                        // Prevent any mouse interactions from closing the dropdown
                        onMouseDown: (e) => {
                          e.stopPropagation();
                        }
                      }}
                    />
                  </MenuItem>
                  
                  {/* Category options */}
                  {loadingCategoryOptions ? (
                    <MenuItem disabled>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', py: 1 }}>
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                        <Typography>Loading categories...</Typography>
                      </Box>
                    </MenuItem>
                  ) : filteredCategoryOptions.length === 0 ? (
                    <MenuItem disabled>
                      <Typography sx={{ textAlign: 'center', width: '100%', py: 1 }}>
                        No categories found
                      </Typography>
                    </MenuItem>
                  ) : (
                    filteredCategoryOptions.map((category) => (
                      <MenuItem 
                        key={category.id} 
                        value={String(category.id)}
                      >
                        {category.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                
                {filteredCategoryOptions.length > 0 && !loadingCategoryOptions && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {filteredCategoryOptions.length} {filteredCategoryOptions.length === 1 ? 'category found' : 'categories found'}
                  </Typography>
                )}
              </FormControl>
              
              <TextField
                fullWidth
                margin="dense"
                size="small"
                label="Title (Required)"
                value={title}
                onChange={handleTitleChange}
                helperText={!title ? "Title is required" : ""}
                error={!title}
                required
              />
              
              <Box sx={{ mt: 1.5, mb: 1 }}>
                <Typography variant="body2" gutterBottom>
                  Keywords (Required - minimum 3)
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={9}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Add keyword (no commas)"
                      value={currentKeyword}
                      onChange={(e) => setCurrentKeyword(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddKeyword();
                        }
                      }}
                      error={currentKeyword.includes(',')}
                      helperText={currentKeyword.includes(',') ? 'Commas are not allowed' : ''}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddKeyword}
                      fullWidth
                      size="small"
                      startIcon={<AddIcon />}
                      disabled={currentKeyword.includes(',')}
                    >
                      Add
                    </Button>
                  </Grid>
                </Grid>
                <Typography variant="caption" color={keywords.length < 3 ? "error" : "textSecondary"} sx={{ mt: 0.5, display: 'block' }}>
                  {keywords.length}/3 keywords minimum ({keywords.length < 3 ? `Add ${3 - keywords.length} more` : 'Minimum reached'})
                </Typography>
              </Box>
              
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {keywords.map((keyword, index) => (
                  <Chip
                    key={index}
                    label={keyword}
                    onDelete={() => handleRemoveKeyword(keyword)}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
              
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="medium"
                  onClick={handleGenerateContent}
                  disabled={loading || !domainId || !categoryId || keywords.length < 3 || loadingDomains || loadingCategoryOptions}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {loading ? 'Generating...' : 'Generate Content with AI'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
        
        {/* Preview Section */}
        <Grid item xs={12} md={7}>
          <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1">
                Generated Content
              </Typography>
              
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyContent}
                  disabled={!generatedContent}
                >
                  Copy
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveContent}
                  disabled={saving || !generatedContent}
                >
                  {saving ? 'Saving...' : 'Save Page'}
                </Button>
              </Stack>
            </Box>
            
            {error && (
              <Box sx={{ mb: 1.5, p: 1.5, bgcolor: '#ffebee', borderRadius: 1 }}>
                {error.includes('\n') ? (
                  <>
                    <Typography color="error" variant="body2" gutterBottom>
                      Please fix the following errors:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: '16px' }}>
                      {error.split('\n').map((err, i) => (
                        <li key={i}>
                          <Typography color="error" variant="caption">{err}</Typography>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <Typography color="error" variant="body2">{error}</Typography>
                )}
              </Box>
            )}
            
            {success && (
              <Box sx={{ mb: 1.5, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                <Typography color="success.main" variant="body2">{success}</Typography>
              </Box>
            )}
            
            {generatedContent ? (
              <Box sx={{ 
                p: 1.5, 
                border: '1px solid #e0e0e0', 
                borderRadius: 1,
                maxHeight: '500px',
                overflowY: 'auto',
                bgcolor: '#ffffff'
              }}>
                <Card variant="outlined" sx={{ mb: 1.5 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Title
                      </Typography>
                      <Chip 
                        icon={<EditIcon />} 
                        label="Edit" 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: '20px' }}
                      />
                    </Box>
                    <TextField
                      fullWidth
                      variant="standard"
                      value={title}
                      onChange={handleTitleChange}
                      sx={{ mb: 2 }}
                    />
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Typography variant="subtitle2" color="text.secondary">
                      Keywords
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                      {keywords.map((keyword, i) => (
                        <Chip key={i} label={keyword} size="small" onDelete={() => handleRemoveKeyword(keyword)} />
                      ))}
                    </Box>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Typography variant="subtitle2" color="text.secondary">
                      Categories
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {categories.map((category, i) => (
                        <Chip key={i} label={category} size="small" color="secondary" />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Introduction
                  </Typography>
                  <Chip 
                    icon={<EditIcon />} 
                    label="Edit" 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  value={intro}
                  onChange={handleIntroChange}
                  size="small"
                />
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Content Sections
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddContentBlock}
                  >
                    Add Section
                  </Button>
                </Box>
                
                {contentBlocks.map((block, index) => (
                  <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">Section {index + 1}</Typography>
                      <Box>
                        <Chip 
                          icon={<EditIcon />} 
                          label="Edit" 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', mr: 1 }}
                        />
                        <Chip 
                          icon={<DeleteIcon />} 
                          label="Remove" 
                          size="small" 
                          color="error" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                          onClick={() => handleRemoveContentBlock(index)}
                          disabled={contentBlocks.length <= 1}
                        />
                      </Box>
                    </Box>
                    
                    <TextField
                      fullWidth
                      label="Header"
                      variant="outlined"
                      value={block.header}
                      onChange={(e) => handleContentBlockChange(index, 'header', e.target.value)}
                      size="small"
                      sx={{ mb: 1 }}
                      error={block.header.length < 5}
                      helperText={block.header.length < 5 ? "Header must be at least 5 characters" : ""}
                    />
                    
                    <TextField
                      fullWidth
                      label="Content"
                      multiline
                      rows={6}
                      variant="outlined"
                      value={block.paragraph}
                      onChange={(e) => handleContentBlockChange(index, 'paragraph', e.target.value)}
                      size="small"
                      error={block.paragraph.length < 250}
                      helperText={block.paragraph.length < 250 ? `Content must be at least 250 characters (${block.paragraph.length}/250)` : ""}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box 
                sx={{ 
                  height: '500px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: '#f5f5f5',
                  borderRadius: 1,
                  border: '1px dashed #bdbdbd'
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  {loading ? 'Generating content...' : 'Generated content will appear here'}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default RsocGenerator;